/**
 * The plays timeline: the whole game, one tap away (BUILD_PLAN 5.7).
 *
 * Rendered on the `Dialog` primitive rather than as a hand-rolled overlay, so
 * focus trapping, escape, scroll locking, hiding the book behind it from a
 * screen reader, and the below-640px swap to a bottom sheet all come from the
 * one implementation the rest of the app uses.
 *
 * **It lists the LIVE plays only.** A voided play is gone from here, which is
 * what makes voiding the same play twice impossible rather than merely
 * discouraged, and it matches what a server read returns: `listEvents` drops
 * every voiding row and the copy for `EVENT_ALREADY_VOIDED` promises that this
 * screen shows the current version.
 *
 * Newest first. A scorekeeper opening this at a dead ball is looking for the
 * play that just happened, not for the tip-off.
 */

import { spokenPlayerLabel } from './player-label.js';
import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@good-season/ui';

import type { LoggedEvent, RosterPlayer } from './db.js';
import {
  FOUL_KINDS,
  clockLabel,
  describePlay,
  isOpeningRow,
  isUnattributed,
  isVoidable,
  namesAPlayer,
  noteTextOf,
} from './game.js';

export interface PlaysTimelineProps {
  readonly open: boolean;
  /** Live plays, oldest first. This reverses them for display. */
  readonly plays: readonly LoggedEvent[];
  /** The players present today, which is who a play can be given to. */
  readonly roster: readonly RosterPlayer[];
  readonly onClose: () => void;
  readonly onVoid: (row: LoggedEvent) => void;
  readonly onAttribute: (row: LoggedEvent, playerId: string) => void;
  /** Change a foul's kind: the same correction as the strip, with no timer. */
  readonly onRetype: (row: LoggedEvent, type: string) => Promise<LoggedEvent | null>;
}

function whenOf(row: LoggedEvent): string {
  return `P${String(row.event.period)} ${clockLabel(row.event.gameClockMs)}`;
}

/**
 * Who a play belongs to, said in a way that distinguishes the three reasons a
 * name can be missing.
 *
 * An opponent play and a team rebound have no player by definition; an
 * unattributed play of ours is a gap somebody is meant to close. Printing all
 * three as a blank makes the badge count look wrong.
 */
function whoOf(
  row: LoggedEvent,
  player: RosterPlayer | undefined,
  roster: readonly RosterPlayer[],
): string {
  if (player !== undefined) return spokenPlayerLabel(player, roster);
  if (row.event.team === 'them') return 'Them';
  return isUnattributed(row) ? 'No player yet' : 'Team';
}

function isFoul(row: LoggedEvent): boolean {
  return FOUL_KINDS.some((kind) => kind.type === row.event.type);
}

export function PlaysTimeline({
  open,
  plays,
  roster,
  onClose,
  onVoid,
  onAttribute,
  onRetype,
}: PlaysTimelineProps) {
  /**
   * The one attributed play whose player is being changed, if any.
   *
   * BUILD_PLAN 5.7: tap any play to re-attribute it. A basket credited to the
   * wrong child could otherwise only be voided and recorded again, at a new
   * clock reading, which moves its plus-minus onto a lineup that was not on
   * the floor when it happened. The chips are shown for one row at a time
   * because forty rows times seven chips is not a list anybody can use.
   */
  const [changing, setChanging] = useState<string | null>(null);
  /** The one foul whose kind is being chosen, if any. */
  const [kindOf, setKindOf] = useState<string | null>(null);
  /** Said politely after a change, because nothing else says it to a screen reader. */
  const [announcement, setAnnouncement] = useState('');
  /**
   * Counts announcements, and keys the node that holds the text. The same
   * words twice ("Foul changed to shooting." for a second foul) are no change
   * to a live region, so nothing would be said; a new node each time is.
   */
  const [announced, setAnnounced] = useState(0);
  /** Inside the Plays dialog, so a listener can tell whether it is the top layer. */
  const contentRef = useRef<HTMLDivElement | null>(null);
  /** The row whose Change kind button takes focus once the list has redrawn. */
  const [refocus, setRefocus] = useState<string | null>(null);
  /** The picker was just opened, so its first kind takes focus. */
  const pickerOpened = useRef(false);

  // A picker or a message left from the last time the dialog was open is not
  // this time's.
  useEffect(() => {
    if (open) return;
    setKindOf(null);
    setAnnouncement('');
    setRefocus(null);
  }, [open]);

  /*
   * Focus has to land somewhere stable, and the row it was on is gone: a
   * change replaces the row (new id, new element), so without this focus falls
   * to the dialog and a keyboard or screen reader user starts the list again.
   * It goes to the changed foul's Change kind button, or the first one if that
   * row is gone too.
   */
  useEffect(() => {
    if (refocus === null) return;
    const exact = document.querySelector<HTMLElement>(`[data-kind-toggle="${refocus}"]`);
    if (exact !== null) {
      exact.focus();
      setRefocus(null);
      return;
    }
    // Not drawn yet: wait for the next redraw. Gone for good: the first one.
    if (plays.some((row) => row.id === refocus)) return;
    document.querySelector<HTMLElement>('[data-kind-toggle]')?.focus();
    setRefocus(null);
  }, [refocus, plays]);

  useEffect(() => {
    if (kindOf === null || !pickerOpened.current) return;
    pickerOpened.current = false;
    document.querySelector<HTMLElement>('[data-kind-choice]')?.focus();
  }, [kindOf]);

  /*
   * Escape collapses an open picker, and leaves the Plays dialog open. The
   * dialog primitive closes on Escape from a capture listener on the document,
   * which a handler on the picker cannot get ahead of, so this listens one
   * step earlier, on the window, and only while a picker is open AND the Plays
   * dialog is the topmost layer. A refusal alert or the Void confirm stacked
   * over it takes the Escape for itself: swallowing it here collapsed the
   * picker behind the modal and left the modal open for a second press.
   */
  useEffect(() => {
    if (kindOf === null) return undefined;
    const collapse = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const layers = document.querySelectorAll('[role="dialog"], [role="alertdialog"]');
      const top = layers.item(layers.length - 1);
      if (contentRef.current === null || !top.contains(contentRef.current)) return;
      event.stopPropagation();
      event.preventDefault();
      setRefocus(kindOf);
      setKindOf(null);
    };
    window.addEventListener('keydown', collapse, true);
    return () => window.removeEventListener('keydown', collapse, true);
  }, [kindOf]);

  async function change(row: LoggedEvent, type: string) {
    const changed = await onRetype(row, type);
    if (changed === null) {
      // Nothing changed, and the screen has said why. The picker stays, so the
      // next try is one tap.
      setRefocus(row.id);
      return;
    }
    const kind = FOUL_KINDS.find((entry) => entry.type === type);
    setKindOf(null);
    setAnnouncement(`Foul changed to ${(kind?.label ?? '').toLowerCase()}.`);
    setAnnounced((count) => count + 1);
    setRefocus(changed.id);
  }

  // Nothing derived while it is shut. The book re-renders four times a second
  // with a running clock, and reversing a whole game each time buys nothing.
  // `Dialog` renders nothing at `open={false}` either, so this changes no
  // behaviour.
  if (!open) return null;

  /*
   * Newest by GAME time, not by when the row was written. BUILD_PLAN 5.8's
   * order is (period, clock remaining desc, wall clock); this is its reverse.
   * Write order put a tip-off basket attributed at halftime above every
   * second-period play, because the named copy carries the newest clientSeq
   * while keeping the original's reading. Found by the fresh-context pass.
   *
   * The opening lineup is setup, not a play anybody tapped. Listing it would
   * put six rows nobody can act on at the bottom of every game.
   */
  const newestFirst = plays
    .filter((row) => !isOpeningRow(row, plays))
    .sort(
      (left, right) =>
        right.event.period - left.event.period ||
        left.event.gameClockMs - right.event.gameClockMs ||
        right.event.clientSeq - left.event.clientSeq,
    );
  const byId = new Map(roster.map((player) => [player.id, player]));

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      open={open}
      size="lg"
      title="Plays"
    >
      <p className="sb__srOnly" role="status">
        <span key={announced}>{announcement}</span>
      </p>
      <div ref={contentRef}>
        {newestFirst.length === 0 ? (
          <p className="sb-plays__empty">
            Nothing recorded yet. Every play you tap shows up here, and you can give it to a player
            or take it back.
          </p>
        ) : (
          <ul className="sb-plays">
            {newestFirst.map((row) => {
              const playerId = row.event.playerId;
              const player = typeof playerId === 'string' ? byId.get(playerId) : undefined;
              const description = describePlay(row);
              const gap = isUnattributed(row);
              const who = whoOf(row, player, roster);
              const note = noteTextOf(row);
              const offering = gap || changing === row.id;

              return (
                <li className={`sb-plays__row${gap ? ' sb-plays__row--gap' : ''}`} key={row.id}>
                  <p className="sb-plays__head">
                    <span className="sb-plays__when">{whenOf(row)}</span>
                    <span className="sb-plays__what">{description}</span>
                    <span className="sb-plays__who">{who}</span>
                    {note === '' ? null : <span className="sb-plays__note">{note}</span>}
                  </p>

                  {offering ? (
                    <div className="sb-plays__give">
                      {/*
                      A shape and a word, not a colour. The row is tinted too,
                      and the rule is that colour is never the only carrier.
                    */}
                      <span className="sb-plays__flag">{gap ? 'Give it to' : 'Move it to'}</span>
                      {roster
                        .filter((option) => option.id !== playerId)
                        .map((option) => (
                          <button
                            /*
                             * The full sentence, because forty of these are on
                             * the screen at once and a button called "12" tells
                             * a screen reader nothing about which play it
                             * changes.
                             */
                            aria-label={`Give ${description} at ${whenOf(row)} to ${spokenPlayerLabel(option, roster)}`}
                            className="sb-plays__chip"
                            key={option.id}
                            onClick={() => {
                              setChanging(null);
                              onAttribute(row, option.id);
                            }}
                            type="button"
                          >
                            <span>{spokenPlayerLabel(option, roster)}</span>
                          </button>
                        ))}
                    </div>
                  ) : null}

                  {!offering && namesAPlayer(row) ? (
                    <button
                      aria-label={`Change player for ${description} at ${whenOf(row)}`}
                      className="sb-plays__chip"
                      onClick={() => setChanging(row.id)}
                      type="button"
                    >
                      Change player
                    </button>
                  ) : null}

                  {isFoul(row) ? (
                    <button
                      aria-expanded={kindOf === row.id}
                      aria-label={`${kindOf === row.id ? 'Cancel changing kind' : 'Change kind'} for ${description} at ${whenOf(row)}`}
                      className="sb-plays__kind"
                      data-kind-toggle={row.id}
                      onClick={() => {
                        if (kindOf === row.id) {
                          setKindOf(null);
                          return;
                        }
                        pickerOpened.current = true;
                        setKindOf(row.id);
                      }}
                      type="button"
                    >
                      {kindOf === row.id ? 'Cancel' : 'Change kind'}
                    </button>
                  ) : null}

                  {isFoul(row) && kindOf === row.id ? (
                    <div className="sb-plays__give">
                      <span className="sb-plays__flag">Change to</span>
                      {FOUL_KINDS.filter((kind) => kind.type !== row.event.type).map((kind) => (
                        <button
                          aria-label={`Change ${description} at ${whenOf(row)} to ${kind.label.toLowerCase()}`}
                          className="sb-plays__kind"
                          data-kind-choice=""
                          key={kind.type}
                          onClick={() => void change(row, kind.type)}
                          type="button"
                        >
                          {kind.label}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {/*
                  Absent, not disabled, on the opening lineup and on a period
                  start: neither can be taken back on its own without leaving
                  the floor or the period in a state no tap can repair. See
                  `isVoidable`.
                */}
                  {isVoidable(row, plays) ? (
                    <button
                      aria-label={`Void ${description} at ${whenOf(row)}`}
                      className="sb-plays__void"
                      onClick={() => onVoid(row)}
                      type="button"
                    >
                      Void
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
